import { useState } from 'react';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

type Props = {
  open: boolean;
  onOpenChange: (aperto: boolean) => void;
};

/**
 * Il link del modulo pubblico di iscrizione ai corsi, pronto da copiare.
 * Se si scrive il nome di un corso, il link lo porta con se': compare in cima
 * al modulo e sul lead, cosi' si sa a quale corso si e' iscritta la persona.
 */
export function LinkModuloCorsiDialog({ open, onOpenChange }: Props) {
  const [corso, setCorso] = useState('');
  const [copiato, setCopiato] = useState(false);

  const nomeCorso = corso.trim();
  const link = `${window.location.origin}/iscrizione-corsi${nomeCorso ? `?corso=${encodeURIComponent(nomeCorso)}` : ''}`;

  async function copia(): Promise<void> {
    try {
      await navigator.clipboard.writeText(link);
      setCopiato(true);
      window.setTimeout(() => setCopiato(false), 2000);
    } catch {
      // Senza permesso sugli appunti si seleziona il testo: basta Cmd/Ctrl+C.
      const campo = document.getElementById('link-modulo-corsi') as HTMLInputElement | null;
      campo?.select();
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Modulo di iscrizione ai corsi</DialogTitle>
          <DialogDescription>
            Pagina pubblica, si compila senza account. Ogni iscrizione arriva qui come lead, diventa un cliente con il
            badge Corsista e un contatto del Mailing.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4 px-1">
          <div>
            <Label htmlFor="nome-corso">Nome del corso (facoltativo)</Label>
            <Input
              id="nome-corso"
              value={corso}
              maxLength={150}
              onChange={(e) => setCorso(e.target.value)}
              placeholder="Es. Aggiornamento RSPP — ottobre 2026"
            />
            <p className="mt-1 text-xs text-gray-500">Compare in cima al modulo e sul lead, alla voce Campagna.</p>
          </div>
          <div>
            <Label htmlFor="link-modulo-corsi">Link da condividere</Label>
            <Input id="link-modulo-corsi" value={link} readOnly onFocus={(e) => e.target.select()} className="font-mono text-xs" />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}>
            <ExternalLink className="mr-2 h-4 w-4" /> Apri
          </Button>
          <Button type="button" onClick={copia}>
            {copiato ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {copiato ? 'Copiato' : 'Copia link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
